import { Tag, TagProps, Tooltip } from "@chakra-ui/react";
import { components } from "../../../shared/schema/schema";

/**
 * Which engine built a submission's week.
 *
 * Taken from the REST schema rather than spelled out again, so the engines the
 * server knows about stay the ones this can render. The GraphQL row types the same
 * column as a plain string, since it is read out of a JSON snapshot rather than a
 * column with a type of its own — `toEngine` below is what narrows it.
 */
export type CoServicePlanEngine = NonNullable<
  components["schemas"]["CoServicePlanImportSchedule"]["engine"]
>;

/**
 * The engines a coordinator can ask for when regenerating a regular plan. "ai" and
 * "past-allocation" are retired and only ever appear on old proposals, so neither is
 * something to choose.
 */
export type CoServicePlanScheduler = Exclude<CoServicePlanEngine, "ai" | "past-allocation">;

const TAGS: Record<
  CoServicePlanEngine,
  { label: string; colorScheme: TagProps["colorScheme"]; tooltip: string }
> = {
  ai: {
    label: "AI",
    colorScheme: "blue",
    tooltip: "Built by the scheduling model",
  },
  "solver-highs": {
    label: "LP",
    colorScheme: "teal",
    tooltip: "Built by the linear-programming solver",
  },
  "past-allocation": {
    label: "Allocation",
    colorScheme: "purple",
    tooltip: "Built by allocating the plan into the patient's existing visits (no longer used)",
  },
};

/**
 * A string from the wire, narrowed to an engine this can draw.
 *
 * Null for a submission scheduled before the engine was recorded, and for one that
 * never reached a scheduler at all — no schedulable authorization, nothing to ask.
 * Both render as no badge, which is the honest answer: nothing built this. An
 * unrecognised value is treated the same way rather than shown raw, because a badge
 * is a claim and this cannot substantiate that one.
 */
export function toEngine(value: string | null | undefined): CoServicePlanEngine | null {
  return value === "ai" || value === "solver-highs" || value === "past-allocation" ? value : null;
}

/**
 * The badge, in the row and in the drawer header.
 *
 * Renders nothing rather than a placeholder when there is no engine to name: a row
 * for a submission still in the queue should look like a row with nothing built yet,
 * not like one built by something unnamed.
 */
export default function CoServicePlanEngineTag({
  engine,
  size,
}: {
  engine: string | null | undefined;
  size?: TagProps["size"];
}) {
  const narrowed = toEngine(engine);
  if (narrowed === null) {
    return null;
  }
  const tag = TAGS[narrowed];
  return (
    <Tooltip label={tag.tooltip}>
      <Tag colorScheme={tag.colorScheme} size={size} variant="subtle">
        {tag.label}
      </Tag>
    </Tooltip>
  );
}
